import { Controller, useForm } from 'react-hook-form';
import cx from 'classnames';
import {
  useAiControllerGetAiConfig,
  useAiControllerUpdateAiConfig,
} from '../../../api/informApi/informApiComponents';
import { SerializedAiConfig } from '../../../api/informApi/informApiSchemas';
import Card from '../../../components/Card';
import Typography from '../../../components/Typography';
import { useUserSession } from '../../../components/UserSession';
import Page from '../../../layouts/DashboardLayout/Page';
import styles from './Ai.module.scss';
import TokenInput from '../../../components/TokenInput';
import { HiOutlineCheck } from 'react-icons/hi';
import toast from 'react-hot-toast';
import { useEffect } from 'react';

const voiceMap: Record<
  SerializedAiConfig['mood'],
  { label: string; description: string }
> = {
  FRIENDLY: {
    label: 'Friendly (Default)',
    description:
      "Thank you for taking the time to share your experience at our Italian restaurant. We're thrilled to hear that you enjoyed your visit and found our pizzas and pastas to be amazing. We also appreciate your kind words...",
  },
  QUIRKY: {
    label: 'Quirky',
    description:
      "Thank you so much for taking the time to leave us such a great review! Our chefs will be thrilled to hear that their hard work is paying off! We're delighted to hear that you were able to snag a reservation for your party of three on a busy Friday evening...",
  },
  HUMOUROUS: {
    label: 'Humourous',
    description:
      "We're so glad you enjoyed your meal with us and that our food and service were up to snuff! Thrilled to hear that you loved the pizza with pesto pasta- it's a bit like a flavor party in your mouth, isn't it? We apologize for the reservation, but...",
  },
  FORMAL: {
    label: 'Formal',
    description:
      'Thank you for taking the time to share your experience with us. We are pleased to hear that you were able to secure a reservation at our restaurant and that you enjoyed our delicious pizzas and pastas. We take great pride in our wine selection...',
  },
  OPTIMISTIC: {
    label: 'Optimistic',
    description:
      "We're delighted to hear you had a memorable experience and we look forward to serving you again in the future. Thrilled to hear that you were able to secure a reservation and enjoyed our amazing pizzas and pastas. We take great pride in...",
  },
};

function Ai() {
  const { currentOrganization } = useUserSession();
  const { data, refetch, isLoading } = useAiControllerGetAiConfig({
    pathParams: { id: currentOrganization! },
  });
  const { mutateAsync: updateConfig, isLoading: isUpdating } =
    useAiControllerUpdateAiConfig();
  const { formState, watch, register, control, getValues, reset } =
    useForm<SerializedAiConfig>({
      defaultValues: {
        blacklist: [],
        whitelist: [],
      },
    });
  const mood = watch('mood');

  useEffect(() => {
    if (!isLoading) reset(data);
  }, [data, isLoading, reset]);

  return (
    <Page
      heading="AI Rules"
      description="Using AI responses will create a more personalized response to each review."
      className={styles.container}
      loading={isLoading}
      actions={[
        {
          async onClick() {
            const state = getValues();
            toast
              .promise(
                updateConfig({
                  pathParams: {
                    id: currentOrganization!,
                  },
                  body: {
                    whitelist: state.whitelist,
                    blacklist: state.blacklist,
                    mood: state.mood!,
                  },
                }),
                {
                  loading: 'Updating AI rules...',
                  success: 'AI rules updated!',
                  error: 'Unable to update AI rules',
                },
              )
              .then(() => refetch());
          },
          children: 'Save Changes',
          buttonColor: 'success',
          variant: 'solid',
          disabled: !formState.isDirty || isUpdating,
          icon: {
            placement: 'left',
            element: HiOutlineCheck,
          },
        },
      ]}
    >
      <Card
        className={styles.voiceCard}
        backgroundColor="background-1"
        as="fieldset"
      >
        <Typography textType="h4" as="h2" className={styles.heading}>
          Choose a voice
        </Typography>
        <Typography textType="p3" as="p" className={styles.description}>
          The voice you choose will be the voice of your responses
        </Typography>
        <ul className={styles.voices}>
          {Object.entries(voiceMap).map(([key, { label, description }]) => (
            <Card
              className={cx(styles.voice, mood === key && styles.active)}
              padding="1rem 0.875rem"
              key={key}
              as="li"
            >
              <input
                className={styles.voiceAction}
                type="radio"
                value={key}
                {...register('mood')}
              />
              <Typography
                textColor="inherit"
                textType="p2"
                textWeight="bold"
                as="label"
              >
                {label}
              </Typography>
              <Typography textColor="inherit" textType="p3" as="p">
                <i>{description}</i>
              </Typography>
            </Card>
          ))}
        </ul>
      </Card>
      <Card className={styles.keywordsCard} backgroundColor="background-1">
        <Typography textType="h4" as="h2" className={styles.heading}>
          Phrases to Use
        </Typography>
        <Typography textType="p3" as="p">
          Press <code className={styles.code}>enter</code> after each phrase or
          word
        </Typography>
        <Controller
          render={({ field }) => (
            <TokenInput
              {...field}
              inputProps={{
                placeholder: 'Press enter after each word or phrase...',
              }}
            />
          )}
          control={control}
          name="whitelist"
        />
      </Card>
      <Card className={styles.keywordsCard} backgroundColor="background-1">
        <Typography textType="h4" as="h2" className={styles.heading}>
          Phrases to Avoid
        </Typography>
        <Typography textType="p3" as="p">
          Press <code className={styles.code}>enter</code> after each phrase or
          word
        </Typography>
        <Controller
          render={({ field }) => (
            <TokenInput
              {...field}
              inputProps={{
                placeholder: 'Press enter after each word or phrase...',
              }}
            />
          )}
          control={control}
          name="blacklist"
        />
      </Card>
    </Page>
  );
}

Ai.routeProps = {
  path: '/ai',
};
export default Ai;
